import dynamic from 'next/dynamic';
import { ComponentProps, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import logTrailDetailsWebtoAppModuleClicked from '@alltrails/amplitude/events/logTrailDetailsWebtoAppModuleClicked';
import useUser from '@alltrails/context/hooks/useUser';
import { isSubscriber } from '@alltrails/context/utils/isSubscriber';
import DropdownMenu from '@alltrails/denali/components/DropdownMenu';
import IconButton from '@alltrails/denali/components/IconButton';
import PlusLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Plus';
import Overflow from '@alltrails/denali/icons/Overflow';
import Upload from '@alltrails/denali/icons/Upload';
import Watch from '@alltrails/denali/icons/Watch';
import { TrailPage } from '@alltrails/trails';
import type TrailDetails from '@alltrails/types/TrailDetails';
import getTrailPageType from '@/utils/getTrailPageType';
import styles from './DesktopNav.module.scss';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });
const HitTheTrailModal = dynamic(() => import('@/components/HitTheTrailModal/HitTheTrailModal'), { ssr: false });
const GarminUpsellModal = dynamic(() => import('@alltrails/upsell/components/GarminUpsellModal/GarminUpsellModal'), { ssr: false });

type SignUpAction = 'export' | 'garmin';

type OverflowMenuVariantProps = {
  trailDetails: TrailDetails;
  trailPage: TrailPage;
};

const OverflowMenuVariant = ({ trailDetails, trailPage }: OverflowMenuVariantProps) => {
  const intl = useIntl();
  const user = useUser();
  const { id } = trailDetails;

  const [isOverflowDropdownOpen, setIsOverflowDropdownOpen] = useState(false);
  const [isHitTheTrailModalOpen, setIsHitTheTrailModalOpen] = useState(false);
  const [hitTheTrailModalSteps, setHitTheTrailModalSteps] = useState<ComponentProps<typeof HitTheTrailModal>['steps']>(['initial']);
  const [signUpModalState, setSignUpModalState] = useState<{ isOpen: false } | { isOpen: true; action: SignUpAction }>({ isOpen: false });
  const [isGarminUpsellModalOpen, setIsGarminUpsellModalOpen] = useState(false);

  const trailPageType = getTrailPageType(trailDetails);

  return (
    <>
      <DropdownMenu
        className={styles.overflowDropdown}
        isOpen={isOverflowDropdownOpen}
        onCloseRequest={() => setIsOverflowDropdownOpen(false)}
        anchor={
          <IconButton
            icon={{ Component: Overflow }}
            onClick={() => setIsOverflowDropdownOpen(!isOverflowDropdownOpen)}
            size="md"
            title={intl.formatMessage({ defaultMessage: 'More options' })}
            variant="default"
          />
        }
        placement="bottom-end"
        options={[
          {
            title: intl.formatMessage({ defaultMessage: 'Send to Garmin' }),
            onClick: () => {
              setIsOverflowDropdownOpen(false);
              logTrailDetailsActionTapped({
                trail_id: id.toString(),
                trail_page_action: 'send to garmin',
                trail_page_location: 'header bar',
                trail_page_type: trailPageType
              });
              logTrailDetailsWebtoAppModuleClicked({
                trail_id: id.toString(),
                was_plus_when_web_to_app_started: isSubscriber(user),
                web_to_app_action: 'send to garmin',
                trail_page_type: trailPageType
              });

              if (!user) {
                setSignUpModalState({ isOpen: true, action: 'garmin' });
                return;
              }

              if (!isSubscriber(user)) {
                setIsGarminUpsellModalOpen(true);
                return;
              }

              setHitTheTrailModalSteps(['garmin']);
              setIsHitTheTrailModalOpen(true);
            },
            leadingIcon: { icon: { Component: Watch } },
            trailingIcon: { customElement: <PlusLogoBadge size="sm" variant="icon" /> },
            className: styles.overflowDropdownItem
          },
          {
            title: intl.formatMessage({ defaultMessage: 'Export map file' }),
            onClick: () => {
              setIsOverflowDropdownOpen(false);
              logTrailDetailsActionTapped({
                trail_id: id.toString(),
                trail_page_action: 'export map file',
                trail_page_location: 'header bar',
                trail_page_type: trailPageType
              });
              logTrailDetailsWebtoAppModuleClicked({
                trail_id: id.toString(),
                was_plus_when_web_to_app_started: isSubscriber(user),
                web_to_app_action: 'export map file',
                trail_page_type: trailPageType
              });

              if (!user) {
                setSignUpModalState({ isOpen: true, action: 'export' });
                return;
              }

              setHitTheTrailModalSteps(['export']);
              setIsHitTheTrailModalOpen(true);
            },
            leadingIcon: { icon: { Component: Upload } },
            className: styles.overflowDropdownItem
          }
        ]}
      />

      {isHitTheTrailModalOpen && (
        <HitTheTrailModal
          includeOpenInApp={false}
          includeSendToGarmin={hitTheTrailModalSteps?.includes('garmin') ?? false}
          includeExportMapFile={hitTheTrailModalSteps?.includes('export') ?? false}
          isOpen={isHitTheTrailModalOpen}
          onCloseRequest={() => {
            setIsHitTheTrailModalOpen(false);
          }}
          steps={hitTheTrailModalSteps}
          title={
            hitTheTrailModalSteps && hitTheTrailModalSteps[0] === 'export' ? (
              <FormattedMessage defaultMessage="Export map file" />
            ) : (
              <FormattedMessage defaultMessage="Send to Garmin" />
            )
          }
          trailDetails={trailDetails}
          trailPage={trailPage}
        />
      )}

      {signUpModalState.isOpen && (
        <SignUpModal
          trigger={signUpModalState.action === 'garmin' ? 'Send Routes To Garmin' : 'Export Map File'}
          closeModal={() => setSignUpModalState({ isOpen: false })}
          isOpen={signUpModalState.isOpen}
          onSuccess={() => {
            setSignUpModalState({ isOpen: false });

            if (signUpModalState.action === 'garmin' && !isSubscriber(user)) {
              setIsGarminUpsellModalOpen(true);
            } else {
              setHitTheTrailModalSteps([signUpModalState.action]);
              setIsHitTheTrailModalOpen(true);
            }
          }}
          upsellTrigger={signUpModalState.action === 'garmin' ? 'Send Routes To Garmin' : 'Export Map File'}
          deeplinkData={{ pageId: id, pageType: 'trail' }}
        />
      )}

      {isGarminUpsellModalOpen && (
        <GarminUpsellModal
          isOpen={isGarminUpsellModalOpen}
          onCloseRequest={() => {
            setIsGarminUpsellModalOpen(false);
          }}
        />
      )}
    </>
  );
};

export default OverflowMenuVariant;
